import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n from '@/lib/i18n/index.js';
import { storageKey } from '@/lib/storage-key.js';

type PrefsState = {
  locale: string;
  setLocale: (l: string) => void;
};

/**
 * PHASE-1-NOTES fix #4: Locale key drift.
 *
 * i18next LanguageDetector reads from localStorage key `${tenantId}.player.locale`
 * (see src/lib/i18n/index.ts detection config). Zustand persist previously
 * only wrote inside `${tenantId}.player.prefs → state.locale`, so language changes
 * from the Settings screen had no effect on the active i18n instance.
 *
 * Fix: setLocale now calls i18n.changeLanguage() AND writes the bare key
 * `${tenantId}.player.locale` to localStorage so the LanguageDetector also picks
 * up the chosen locale on next page load.
 */
export const usePrefsStore = create<PrefsState>()(
  persist(
    (set) => ({
      locale: 'en',
      setLocale: (locale) => {
        set({ locale });
        // Sync i18next immediately
        void i18n.changeLanguage(locale);
        // Sync the bare localStorage key that LanguageDetector reads
        try {
          localStorage.setItem(storageKey('locale'), locale);
        } catch {
          // Ignore storage errors (private mode, quota exceeded, etc.)
        }
        // Keep <html lang> in sync for accessibility and browser tools
        document.documentElement.lang = locale;
      },
    }),
    {
      name: storageKey('prefs'),
    },
  ),
);
